<template>
  <div class="order-summary-preview">
    <h5 class="text-uppercase">
      Order Summary Preview
    </h5>
    <div v-if="showWholesalePrice" class="total-price">
      <div class="fw-bold">
        Total
      </div>
      <div class="fw-bold">
        {{ formatCurrency(activeDraftOrderSummary?.total_amount) }}
      </div>
    </div>

    <div
      v-if="loadingPopper"
      class="d-flex justify-content-center mt-3"
    >
      <moon-loader
        color="black"
        size="85px"
      />
    </div>
    <ul
      v-else
      class="list-group"
    >
      <li
        v-for="(orderItem, index) in activeDraftOrderSummary?.items"
        :key="orderItem.id || index"
        class="list-group-item"
      >
        <div class="d-flex align-items-center">
          <a
            class="icon cursor-pointer"
            @click="onDeleteProductClicked(orderItem)"
          >
            <fa
              icon="trash-alt"
              style="color: #c01b0d"
              fixed-width
            />
          </a>
          <div class="product-detail">
            <img
              class="product-detail__image"
              :key="orderItem.product.image_variants?.thumb_webp || orderItem.product.image_url"
              :src="orderItem.product.image_variants?.thumb_webp || orderItem.product.image_url"
              :alt="orderItem.product.name"
              loading="lazy"
              decoding="async"
              @error="$event.target.src = orderItem.product.image_url"
            >
            <div>
              <h6 class="product-detail__name fw-bold text-start">
                {{ orderItem.product.name }}
              </h6>
              <div class="product-detail__qty text-start">
                <label class="fw-bold">Qty: </label>
                <span>{{ quantity(orderItem) }}</span>
              </div>
              <div v-if="showWholesalePrice" class="product-detail__cost text-start">
                <label class="fw-bold">Cost: </label>
                <span :class="orderItem.discount_amount != 0 ? 'text-decoration-line-through' : ''">
                      {{ formatCurrency(orderItem.product_price) }}
                </span>
                <span />
                <span
                      v-if="orderItem.discount_amount != 0"
                      class="text-danger"
                >
                      {{ formatCurrency(orderItem.product_discount_price) }}
                </span>

              </div>
              <div v-if="showWholesalePrice" class="product-detail__total text-start">
                <label class="fw-bold">Total: </label>
                <span>{{ formatCurrency(productTotal(orderItem)) }} </span>

              </div>
            </div>
          </div>
        </div>
      </li>
    </ul>
    <div class="action-button">
      <p-button
        type="button"
        full-width
        :disabled="!activeDraftOrder"
        @click="proceedToCheckout"
      >
        Proceed To Checkout
      </p-button>
      <div
        v-if="!activeDraftOrder"
        class="no-order-warning bg-warning rounded-2 p-1 px-2 mt-2"
      >
        Create new order or select draft order to proceed
      </div>
    </div>
  </div>
</template>

<script>
import MoonLoader from 'vue-spinner/src/MoonLoader.vue'
import { formatUSDCurrency } from '~/helpers/format'
import PButton from '~/components/core/PButton'
import { mapGetters, mapActions, mapState } from 'vuex'
import wholesalePriceVisibility from '~/mixins/wholesalePriceVisibility'

export default {
  name: 'OrderSummaryPreview',
  components: {
    PButton,
    MoonLoader
  },
  mixins: [wholesalePriceVisibility],
  props: {
    loadingPopper: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
          viewTypeState: state => state.catalog_products.viewType,
    }),
    ...mapGetters({
      catalogProducts: 'catalog_products/catalogProducts',
      activeDraftOrder: 'orders/activeDraftOrder'
    }),
    ...mapState({
          stateActionRequiredSelectedOption: (state) => state.orders.actionRequiredSelectedOption,
    }),

    activeDraftOrderSummary () {
      if (!this.activeDraftOrder) {
        return null
      }
      return {
        items: this.activeDraftOrder.order_has_products,
        total_amount: this.isRouteValid()
              ? this.activeDraftOrder.available_quantity_total
              : this.activeDraftOrder.total

      }
    }
  },
  methods: {
    ...mapActions({
      removeProduct: 'orders/removeProduct',
      fetchOrders: 'orders/fetchOrders',
      fetchCatalogProducts: 'catalog_products/fetchCatalogProducts',
      toggleCatalogProductsProperty: 'catalog_products/toggleCatalogProductsProperty',
      fetchProductItemsOrderSummary: 'checkout/fetchProductItemsOrderSummary',
    }),
    formatCurrency (amount) {
      return formatUSDCurrency(amount)
    },
    async proceedToCheckout () {
      this.$emit('onProceedToCheckoutClicked')
      localStorage.setItem('isAdmin', false)
      localStorage.setItem('active_admin_view', false)
      const event = new Event('active_admin_view');
      document.dispatchEvent(event);
      this.$router.push({ name: 'checkout-proceed.order-summary' })
    },
    async onDeleteProductClicked (item) {
      await this.removeProduct({
        orderId: this.activeDraftOrder.id,
        productId: item.product_id
      })
      await this.fetchOrders()

      if (this.viewTypeState === 'detail') {
          const params = { ...this.$route.query }
          params.page = this.currentPage
          await this.fetchCatalogProducts({ urlEndpoint: `/api/product?${new URLSearchParams(params).toString()}` })
      } else {
          this.toggleCatalogProductsProperty({
             property: 'active_order',
             products: [this.catalogProducts.find(product => product.id === item.product_id)]
          })
      }

      if (this.$route.name === 'tag-module' || this.$route.name === 'products') {
        const { selected, ...filteredParams } = { ...this.$route.query }
        selected === 'my_tags' ? delete filteredParams.tag_ids : delete filteredParams.my_tags_only
        filteredParams.page = 1
        const apiRoute = `/api/product?${new URLSearchParams(filteredParams).toString()}`
        this.fetchCatalogProducts({ urlEndpoint: apiRoute, isChange: true })
      }

      if (this.$route.name === 'checkout-proceed.order-summary') {
          await this.fetchProductItemsOrderSummary()
      }

    },
    isRouteValid() {
          const validRoutes = [
              'checkout-proceed.order-review',
              'checkout-proceed.delivery-options',
              'checkout-proceed.payment-details'
          ];
          return validRoutes.includes(this.$route.name);
      },
    quantity(product) {
          return this.isRouteValid() && product.backorder_quantity === 0
              ? product?.available_quantity
              : product?.quantity;
      },
    productTotal(product) {
          return this.isRouteValid() && product.backorder_quantity === 0
              ? product?.available_quantity_total
              : product?.total;
      },
  },
}
</script>

<style lang="scss" scoped>
.order-summary-preview {
  max-width: 363px;
  max-height: 641px;

  h5 {
      font: normal normal bold 19px/25px PlayfairDisplay-Bold;
      text-align: left;
      margin-bottom: 21px;
      padding-left: 32px;
  }

  .total-price {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 33px;
    padding-bottom: 8px;
    border-bottom: 2px solid #dadada;
    > div {
      font: normal normal bold 14px/19px PlayfairDisplay-Bold;
    }
  }

  .list-group {
    max-height: 450px;
    overflow-y: scroll;

    .list-group-item {
      border: none;
      padding: 0px;

      .icon {
        font-size: 18px;
        margin-right: 10px;
      }
      &:first-child {
        .product-detail {
          border-top: none;
        }
      }
      .product-detail {
          display: flex;
          border-bottom: 2px solid #dadada;
          letter-spacing: 0px;
          padding: 24px 0px;
          flex: 1;

        &__image {
          width: 78px;
          height: 86px;
          margin-right: 15px;
        }
        &__name {
          font: normal normal bold 14px/16px Arial;
          margin-bottom: 13px;
        }
        &__qty, &__cost, &__total {
          font: normal normal normal 13px/15px Arial;
          padding-bottom: 5px;
          label {
            font: normal normal bold 13px/15px Arial;
          }
        }
        &__total {
          padding-bottom: 0px;
        }
      }
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .action-button {
    padding-left: 33px;
    padding-top: 15px;

    button {
      padding-top: 13px;
      padding-bottom: 13px;
      font: normal normal bold 14px/16px Arial;
    }
  }
  .no-order-warning{
    font-size: .625rem;
    font-weight: 500;
  }
}
</style>
